import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Github, Loader2 } from 'lucide-react';
import { linkGitHubApp } from '../api';

// Where GitHub returns the browser after the GitHub App sign-in started from Agent
// Settings: /github/setup?code=…&state=…. The server trades the code for proof of which
// installations this person can see and links those to the org they started from.

// GitHub's code works exactly once, and React may start the effect below twice for the
// same code (development's double run); the second exchange would be refused and show an
// error right after the first succeeded. So each code is sent once per page load.
const sentCodes = new Set();

function firstUseOf(code) {
  if (!code || sentCodes.has(code)) return false;
  sentCodes.add(code);
  return true;
}

export default function GitHubSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get('code') || '';
  const state = params.get('state') || '';
  const [error, setError] = useState('');
  const [nothingInstalled, setNothingInstalled] = useState(false);
  const back = localStorage.getItem('github_setup_return') || '/agent-settings';
  const [installUrl] = useState(() => localStorage.getItem('github_install_url'));

  useEffect(() => {
    if (!code || !state || !firstUseOf(code)) return;
    linkGitHubApp(code, state)
      .then(({ data }) => {
        if (!data.accounts.length) { setNothingInstalled(true); return; }
        localStorage.removeItem('github_setup_return');
        localStorage.removeItem('github_install_url');
        navigate(back, { replace: true, state: { tab: 'settings', githubNotice: `GitHub connected (${data.accounts.join(', ')})` } });
      })
      .catch((e) => setError(e.response?.data?.detail || "Couldn't link GitHub"));
  }, [code, state, back, navigate]);

  return (
    <div className="max-w-md mx-auto py-16">
      <h1 className="font-display text-xl font-bold text-fg flex items-center gap-2 mb-2">
        <Github className="w-5 h-5 text-orange" />Linking GitHub
      </h1>
      {!code || !state ? (
        <p className="text-sm text-fg-muted">Nothing to set up. Connect the GitHub App from Agent Settings.</p>
      ) : error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : nothingInstalled ? (
        <div className="space-y-3">
          <p className="text-sm text-fg">The App isn't installed on any GitHub organization you can see.</p>
          {installUrl && (
            <a href={installUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-orange hover:underline">
              Install it on GitHub →
            </a>
          )}
          <p className="text-xs text-fg-muted">After installing, click Connect GitHub App again.</p>
          <button onClick={() => navigate(back, { replace: true, state: { tab: 'settings' } })}
            className="px-3 py-2 rounded-lg border border-panel-border text-fg-muted hover:text-fg text-sm transition-colors">
            Back to settings
          </button>
        </div>
      ) : (
        <p className="text-sm text-fg-muted flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />Checking with GitHub…
        </p>
      )}
    </div>
  );
}
